/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from "@emotion/core";
import { type FC } from "react";
import type { CommentThread, ConversationThread } from "@certa/types";
import { ThreadContext, useCommentsContext } from "../../comments.context";
import { CommentFormNew } from "./CommentFormNew";
import { NoComments } from "./NoComments";
import { useIntl } from "react-intl";
import { MessageViewIdentifier } from "./MessageViewNew";
import { useMentions } from "../../hooks/useMentions";
import { NewThreadForm } from "./NewThreadForm";
import { css } from "emotion";
import { Stack } from "@certa/catalyst/layouts/Stack";

import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import type { NewThreadResponse } from "@certa/queries/types/comments.types";
import { useOptimisticMessages } from "../../hooks/useOptimisticMessages";
import { useCommentsDrawerContext } from "../../CommentsDrawerContext";

type ThreadContainerProps = {
  thread: CommentThread;
  activeConversationThreadComms: ConversationThread | null;
  showNewThread: boolean;
  onThreadCreate: (payload: NewThreadResponse) => void;
  onThreadCancel: () => void;
};

const ThreadContainer: FC<ThreadContainerProps> = ({
  thread,
  activeConversationThreadComms,
  showNewThread,
  onThreadCreate,
  onThreadCancel
}) => {
  const intl = useIntl();
  const { isReadOnly } = useCommentsContext();
  const { isNonEditable } = useCommentsDrawerContext();

  const { isMobileResolution } = useScreenResolution();
  const mentions = useMentions(thread?.threadId, true);
  const onCommentPost = () => showNewThread && onThreadCancel();

  // Shared optimistic messages state for both MessageView and CommentForm
  const {
    optimisticMessages,
    addOptimisticMessage,
    removeOptimisticMessage,
    replaceOptimisticMessage
  } = useOptimisticMessages();

  return (
    <ThreadContext.Provider value={thread}>
      <Stack
        padding={isMobileResolution ? "0" : "0 24"}
        dangerouslySetClassName={css({
          flex: "1 1 0%",
          overflowY: "hidden"
        })}
      >
        {showNewThread ? (
          <NewThreadForm
            onNewThread={onThreadCreate}
            onCancel={onThreadCancel}
          />
        ) : thread?.messageCount > 0 ? (
          <MessageViewIdentifier
            activeConversationThreadComms={activeConversationThreadComms}
            groupedMessages={thread?.groupedMessages}
            mentions={mentions}
            optimisticMessages={optimisticMessages}
          />
        ) : (
          <NoComments
            text={intl.formatMessage({
              id: "comments.noCommentsToShowForThread",
              defaultMessage: "No comments to show for this thread"
            })}
          />
        )}
      </Stack>
      {!isReadOnly && !showNewThread && !isNonEditable && (
        <div
          className={css({
            backgroundColor: "var(--colors-neutral-100)",
            position: "sticky",
            bottom: "0",
            zIndex: 2,
            padding: isMobileResolution
              ? "var(--space-16) var(--space-4)"
              : "var(--space-16)"
          })}
        >
          <CommentFormNew
            onCommentPost={onCommentPost}
            addOptimisticMessage={addOptimisticMessage}
            removeOptimisticMessage={removeOptimisticMessage}
            replaceOptimisticMessage={replaceOptimisticMessage}
          />
        </div>
      )}
    </ThreadContext.Provider>
  );
};

export { ThreadContainer };
